"use client";
import { FC, useMemo, useState } from "react";
import IconChevronDown from "@/assets/icons/down-arrow.svg?react";
import { CheckoutLines } from "@/common/lib/types";
import { parseMeta } from "@/utils/lensConfigurator";
import { formatDiscountPercentLabel, getDisplayDiscountPercent } from "@/apiUtils/productUtils";
import { metadataKeys } from "@/components/lcp/resource/config";
import { prescriptionConstants } from "@/apiUtils/constants";
import RenderPrice from "@/components/productPrice/RenderPrice";
import Accordion from "@/components/ui/accordion/Accordion";
import { cn } from "@/lib/tailwind";
import RenderCurrencyInText from "@/components/common/RenderCurrencyInText";
import { getPromotionBadgeNameFromPromotions } from "@/utils/promotionUtils";

interface CartItemFeaturesProps {
  lineItem: CheckoutLines;
  translations: Record<string, string> | undefined;
  price: number;
  priceUndiscounted: number;
  currency: string;
  language: string;
}

const CartItemFeatures: FC<CartItemFeaturesProps> = ({
  lineItem,
  currency,
  language,
  translations,
  price,
  priceUndiscounted,
}) => {
  const { lensType, lens, additionaloptions, prescription } = useMemo(() => {
    const meta = parseMeta(lineItem.metadata, language);
    const lensType = meta?.[metadataKeys.LENS_TYPE];
    const lens = meta?.[metadataKeys.LENS];
    const additionaloptions = meta?.[metadataKeys.ADDITIONAL_OPTIONS];
    const prescription = meta?.[metadataKeys.PRESCRIPTION];
    return { lensType, lens, additionaloptions, prescription };
  }, [lineItem.metadata, language]);

  const [collapse, setCollapse] = useState(false);

  const handleCollapse = () => {
    setCollapse((prev) => !prev);
  };

  const addonPricing = lineItem.addOns?.find(
    (addon) => addon?.variant?.pricing?.discountPercent !== undefined,
  )?.variant?.pricing;
  const discount = getDisplayDiscountPercent(addonPricing);

  const lensPromotionBadgeName = useMemo(() => {
    for (const addon of lineItem.addOns || []) {
      const fromApplied = getPromotionBadgeNameFromPromotions(addon?.appliedPromotions, language);
      if (fromApplied) return fromApplied;
      const fromAvailable = getPromotionBadgeNameFromPromotions(addon?.variant?.availablePromotions, language);
      if (fromAvailable) return fromAvailable;
    }
    return "";
  }, [lineItem.addOns, language]);

  return (
    <div className="flex justify-between">
      <div className="">
        {lensPromotionBadgeName ? (
          <p className="pt-4 text-sm font-semibold text-secondary-1">
            <RenderCurrencyInText text={lensPromotionBadgeName} />
          </p>
        ) : null}
        <Accordion
          expandIconRotation="rotate-180"
          expandIcon={<> </>}
          onToggleItem={handleCollapse}
          items={[
            {
              classNames: {
                trigger: cn("border-b-0 pb-[10px]", lensPromotionBadgeName ? "pt-2" : "pt-[16px]"),
                container: "border-none",
              },
              title: (
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold">
                    {translations?.cart_selected_lens_label ?? "Selected Lenses"}
                  </p>
                  <IconChevronDown className={cn("transition-all", collapse && "-rotate-180")} />
                </div>
              ),
              content: (
                <div className="bg-white pb-4">
                  {lensType ? (
                    <div key={lensType.key}>
                      <div className="text-sm">
                        <span className="text-sm font-semibold">
                          {translations?.lens_config_lens_options_header_section_title}:
                        </span>
                        &nbsp;{lensType?.choice}
                        {lensType?.subchoice ? <p className="text-sm">{lensType.subchoice}</p> : ""}
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                  {lens && lens.choice ? (
                    <div key={lens.key}>
                      <div className="text-sm">
                        <span className="text-sm font-semibold">
                          {translations?.lens_config_bundles_header_section_title}:
                        </span>
                        &nbsp;{lens?.choice}
                        {lens?.subchoice ? <p className="text-sm">{lens.subchoice}</p> : ""}
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                  {additionaloptions ? (
                    <div key={additionaloptions.key}>
                      <div className="flex flex-wrap items-center gap-0">
                        <div className="mr-0 flex-none pr-0 font-semibold">
                          {translations?.lens_config_additional_options_header_section_title}:
                        </div>
                        <div className="flex-auto p-0 text-sm">
                          &nbsp;{additionaloptions?.choice}
                          {additionaloptions?.subchoice ? (
                            additionaloptions?.color ? (
                              <div className="color-variants text-right">
                                <div
                                  className="inline-block h-4 w-4 rounded-full"
                                  style={{ backgroundColor: additionaloptions.color }}
                                />
                                <span className="ml-2 text-sm">{additionaloptions.subchoice}</span>
                              </div>
                            ) : (
                              <p className="text-sm">{additionaloptions.subchoice}</p>
                            )
                          ) : (
                            ""
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                  {prescription ? (
                    <div key={prescription.key}>
                      <div className="text-sm">
                        <span className="text-sm font-semibold">
                          {translations?.lens_config_save_prescription_header_section_title}:
                        </span>
                        &nbsp;
                        {translations?.[prescriptionConstants[prescription.choice]] ||
                          prescription.choice}
                        {prescription?.subchoice ? (
                          <p className="text-sm">{prescription.subchoice}</p>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>
      {priceUndiscounted !== price ? (
        <div className="py-[16px] min-w-max">
          <div className="md:flex md:gap-2 text-end">
            <p className="text-sm font-normal text-tertiary-6">
              <RenderPrice
                price={priceUndiscounted}
                currencyCode={currency}
                lang={language}
                isStrikethrough
              />
            </p>
            <p className={cn("text-sm font-semibold", "text-secondary-1")}>
              <RenderPrice price={price} currencyCode={currency} lang={language} />
            </p>
            {/* TODO(QA): When Qatar (qa) is enabled, gate this % with NEXT_PUBLIC_PERCENTAGE_SYMBOL_HIDE_COUNTRIES
                via displayPercentage (same as Magrabi CartItemFeatures). */}
            <p className="text-sm">{formatDiscountPercentLabel(discount)}</p>
          </div>
        </div>
      ) : (
        <p className="py-[16px] text-sm font-semibold">
          <RenderPrice price={price} currencyCode={currency} lang={language} />
        </p>
      )}
    </div>
  );
};

export default CartItemFeatures;
